import { useFetch } from '../_hooks/useFetch'
import { usePost } from '../_hooks/usePost'
import { useDelete } from '../_hooks/useDelete'
import _endpoints from '../_endpoints'
import {
  FeatureGate,
  FeatureGateGroup,
  FeatureGateGroupAccess,
  FeatureGateGroupMember,
  FeatureGateUserAccess,
  FeatureGatePayload,
  FeatureGroupPayload
} from '../views/_pages/FeatureGates/featureGateTypes'

const normalizeEmail = (email: string): string => email?.trim().toLowerCase()

export const useFeatureGateService = () => {
  const [isFetchingGates, getGates] = useFetch()
  const [, getGroupList] = useFetch()
  const [isFetchingMembers, getMemberList] = useFetch()
  const [isFetchingGateUsers, getGateUserList] = useFetch()
  const [isFetchingGateGroups, getGateGroupList] = useFetch()
  const [isSaving, postData] = usePost()
  const [isDeleting, deleteData] = useDelete('invdesktop')
  const [isGettingFlagValue, getFlagValue] = useFetch()

  const getFeatureGates = async (): Promise<FeatureGate[]> => {
    const response = await getGates(_endpoints.featureGates.list)
    const gates = response?.items ?? response?.featureGates ?? response
    return Array.isArray(gates) ? gates : []
  }

  const createGate = async (payload: FeatureGatePayload) => {
    return postData({
      method: 'post',
      url: _endpoints.featureGates.create,
      data: payload
    })
  }

  const updateGate = async (id: number, payload: FeatureGatePayload) => {
    return postData({
      method: 'put',
      url: _endpoints.featureGates.update.replace(':id', String(id)),
      data: payload
    })
  }

  const deleteGate = async (id: number) => {
    return deleteData({
      url: _endpoints.featureGates.delete.replace(':id', String(id)),
      type: 'json'
    })
  }

  const getGateUsers = async (id: number): Promise<FeatureGateUserAccess[]> => {
    const response = await getGateUserList(
      _endpoints.featureGates.gateUsers.replace(':id', String(id))
    )
    const users = response?.items ?? response?.users ?? response
    if (!Array.isArray(users)) return []
    return users.map((user: any, index: number) => ({
      id: user?.id ?? index,
      featureGateId: user?.featureGateId ?? id,
      email:
        typeof user === 'string'
          ? user
          : user?.email ??
            user?.userEmail ??
            user?.emailAddress ??
            user?.userPrincipalName ??
            user?.upn ??
            '',
      isEnabled: typeof user?.isEnabled === 'boolean' ? user.isEnabled : true
    }))
  }

  const getFeatureGateValue = async (key: string): Promise<boolean> => {
    const response = await getFlagValue(
      _endpoints.featureGates.getFeatureValue.replace(':key', key)
    )
    return response?.enabled || false
  }

  const getGateGroups = async (id: number): Promise<FeatureGateGroupAccess[]> => {
    const response = await getGateGroupList(
      _endpoints.featureGates.gateGroups.replace(':id', String(id))
    )
    const accessGroups = response?.items ?? response?.groups ?? response
    if (!Array.isArray(accessGroups)) return []
    return accessGroups.map((access: any, index: number) => ({
      id: access?.id ?? index,
      featureGateId: access?.featureGateId ?? id,
      featureGateGroupId: access?.featureGateGroupId ?? access?.groupId ?? access?.id,
      groupName: access?.groupName ?? access?.name,
      isEnabled: typeof access?.isEnabled === 'boolean' ? access.isEnabled : true
    }))
  }

  const setUserAccess = async (id: number, email: string, isEnabled: boolean) => {
    return postData({
      method: 'put',
      url: _endpoints.featureGates.setUserAccess
        .replace(':id', String(id))
        .replace(':email', encodeURIComponent(normalizeEmail(email))),
      data: { isEnabled }
    })
  }

  const removeUserAccess = async (id: number, email: string) => {
    return deleteData({
      url: _endpoints.featureGates.removeUserAccess
        .replace(':id', String(id))
        .replace(':email', encodeURIComponent(normalizeEmail(email))),
      type: 'json'
    })
  }

  const getGroups = async (): Promise<FeatureGateGroup[]> => {
    const response = await getGroupList(_endpoints.featureGates.groups)
    const groups = response?.items ?? response?.groups ?? response
    return Array.isArray(groups) ? groups : []
  }

  const createGroup = async (payload: FeatureGroupPayload) => {
    return postData({
      method: 'post',
      url: _endpoints.featureGates.createGroup,
      data: payload
    })
  }

  const deleteGroup = async (groupId: number) => {
    return deleteData({
      url: _endpoints.featureGates.deleteGroup.replace(':id', String(groupId)),
      type: 'json'
    })
  }

  const getGroupMembers = async (groupId: number): Promise<FeatureGateGroupMember[]> => {
    const response = await getMemberList(
      _endpoints.featureGates.groupMembers.replace(':id', String(groupId))
    )
    const members = response?.items ?? response?.members ?? response
    if (!Array.isArray(members)) return []
    return members
      .map((member: any, index: number) => {
        const email =
          typeof member === 'string'
            ? member
            : member?.email ??
              member?.userEmail ??
              member?.emailAddress ??
              member?.userPrincipalName ??
              member?.upn ??
              ''
        return {
          id: member?.id ?? index,
          featureGateGroupId: member?.featureGateGroupId ?? groupId,
          email
        }
      })
      .sort((a, b) => a.id - b.id)
  }

  const addGroupMember = async (groupId: number, email: string) => {
    return postData({
      method: 'put',
      url: _endpoints.featureGates.addGroupMember
        .replace(':id', String(groupId))
        .replace(':email', encodeURIComponent(normalizeEmail(email))),
      data: {}
    })
  }

  const addGroupMembers = async (groupId: number, emails: string[]) => {
    return postData({
      method: 'post',
      url: _endpoints.featureGates.addGroupMembers.replace(':id', String(groupId)),
      data: { emails: emails ?? [] }
    })
  }

  const removeGroupMember = async (groupId: number, email: string) => {
    return deleteData({
      url: _endpoints.featureGates.removeGroupMember
        .replace(':id', String(groupId))
        .replace(':email', encodeURIComponent(normalizeEmail(email))),
      type: 'json'
    })
  }

  const setGroupAccess = async (id: number, groupId: number, isEnabled: boolean) => {
    return postData({
      method: 'put',
      url: _endpoints.featureGates.setGroupAccess
        .replace(':id', String(id))
        .replace(':groupId', String(groupId)),
      data: { isEnabled }
    })
  }

  const removeGroupAccess = async (id: number, groupId: number) => {
    return deleteData({
      url: _endpoints.featureGates.removeGroupAccess
        .replace(':id', String(id))
        .replace(':groupId', String(groupId)),
      type: 'json'
    })
  }

  return {
    isFetchingGates,
    isFetchingMembers,
    isFetchingGateUsers,
    isFetchingGateGroups,
    isSaving,
    isDeleting,
    getFeatureGates,
    createGate,
    updateGate,
    deleteGate,
    getGateUsers,
    getGateGroups,
    setUserAccess,
    removeUserAccess,
    getGroups,
    createGroup,
    deleteGroup,
    getGroupMembers,
    addGroupMember,
    addGroupMembers,
    removeGroupMember,
    setGroupAccess,
    removeGroupAccess,
    isGettingFlagValue,
    getFeatureGateValue
  }
}
